// Pricing service layer for scenario pricing
// Will later integrate with PPE / Optimal Blue / custom pricing engine

/**
 * Mock pricing engine - generates realistic pricing scenarios
 * @param {Object} inputs - Pricing inputs
 * @returns {Promise<Array>} Array of pricing scenarios
 */
export const runPricingEngine = async (inputs) => {
  // Simulate API call delay
  return new Promise((resolve) => {
    setTimeout(() => {
      const scenarios = generateMockScenarios(inputs);
      resolve(scenarios);
    }, 1500);
  });
};

/**
 * Generates mock pricing scenarios based on inputs
 * @param {Object} inputs - Pricing inputs
 * @returns {Array} Array of pricing scenarios
 */
const generateMockScenarios = (inputs) => {
  const { creditScore, loanAmount, propertyValue, loanPurpose, occupancy, propertyState } = inputs;
  
  const ltv = ((loanAmount / propertyValue) * 100).toFixed(2);
  const baseRate = calculateBaseRate(creditScore, ltv, occupancy);
  
  const scenarios = [];
  
  // Conventional 30-Year Fixed
  if (ltv <= 97 && creditScore >= 620) {
    scenarios.push({
      id: 'conv-30-fixed',
      productName: 'Conventional 30-Year Fixed',
      programType: 'Conventional',
      term: 360,
      amortizationType: 'Fixed',
      interestRate: baseRate,
      apr: baseRate + 0.125,
      points: 0,
      credits: loanAmount * 0.005,
      monthlyPayment: calculateMonthlyPayment(loanAmount, baseRate, 360),
      closingCosts: loanAmount * 0.02,
      eligible: true,
      eligibilityNotes: 'Meets all conventional guidelines',
      pmi: ltv > 80 ? (loanAmount * 0.0055 / 12) : 0,
      features: ['No prepayment penalty', 'Assumable with lender approval', 'Standard documentation']
    });
  } else {
    scenarios.push({
      id: 'conv-30-fixed',
      productName: 'Conventional 30-Year Fixed',
      programType: 'Conventional',
      eligible: false,
      eligibilityNotes: ltv > 97 ? 'LTV exceeds 97% maximum' : 'Credit score below 620 minimum',
      ineligibleReasons: [
        ltv > 97 ? 'LTV too high' : null,
        creditScore < 620 ? 'Credit score too low' : null
      ].filter(Boolean)
    });
  }
  
  // Conventional 15-Year Fixed
  if (ltv <= 97 && creditScore >= 620) {
    scenarios.push({
      id: 'conv-15-fixed',
      productName: 'Conventional 15-Year Fixed',
      programType: 'Conventional',
      term: 180,
      amortizationType: 'Fixed',
      interestRate: baseRate - 0.625,
      apr: baseRate - 0.500,
      points: 0,
      credits: loanAmount * 0.0075,
      monthlyPayment: calculateMonthlyPayment(loanAmount, baseRate - 0.625, 180),
      closingCosts: loanAmount * 0.02,
      eligible: true,
      eligibilityNotes: 'Meets all conventional guidelines',
      pmi: ltv > 80 ? (loanAmount * 0.0045 / 12) : 0,
      features: ['Lower rate', 'Build equity faster', 'Save on total interest']
    });
  }
  
  // FHA 30-Year Fixed
  if (ltv <= 96.5 && creditScore >= 580) {
    scenarios.push({
      id: 'fha-30-fixed',
      productName: 'FHA 30-Year Fixed',
      programType: 'FHA',
      term: 360,
      amortizationType: 'Fixed',
      interestRate: baseRate + 0.25,
      apr: baseRate + 0.375,
      points: 0,
      credits: loanAmount * 0.003,
      monthlyPayment: calculateMonthlyPayment(loanAmount, baseRate + 0.25, 360),
      closingCosts: loanAmount * 0.025,
      eligible: true,
      eligibilityNotes: 'FHA approved',
      mip: loanAmount * 0.0085 / 12, // Monthly MIP
      upfrontMip: loanAmount * 0.0175,
      features: ['Lower down payment', 'Flexible credit', 'FHA insured']
    });
  }
  
  // VA 30-Year Fixed
  if (occupancy === 'Primary') {
    scenarios.push({
      id: 'va-30-fixed',
      productName: 'VA 30-Year Fixed',
      programType: 'VA',
      term: 360,
      amortizationType: 'Fixed',
      interestRate: baseRate - 0.125,
      apr: baseRate,
      points: 0,
      credits: loanAmount * 0.01,
      monthlyPayment: calculateMonthlyPayment(loanAmount, baseRate - 0.125, 360),
      closingCosts: loanAmount * 0.015,
      eligible: true,
      eligibilityNotes: 'Subject to VA eligibility verification',
      fundingFee: loanAmount * 0.0215,
      features: ['No down payment', 'No PMI', 'VA guaranteed', 'Competitive rates']
    });
  }
  
  // Jumbo 30-Year Fixed
  if (loanAmount > 766550 && creditScore >= 700 && ltv <= 80) {
    scenarios.push({
      id: 'jumbo-30-fixed',
      productName: 'Jumbo 30-Year Fixed',
      programType: 'Jumbo',
      term: 360,
      amortizationType: 'Fixed',
      interestRate: baseRate + 0.375,
      apr: baseRate + 0.500,
      points: 1,
      credits: 0,
      monthlyPayment: calculateMonthlyPayment(loanAmount, baseRate + 0.375, 360),
      closingCosts: loanAmount * 0.025,
      eligible: true,
      eligibilityNotes: 'Meets jumbo requirements',
      features: ['High balance', 'Competitive jumbo rates', 'Strong credit required']
    });
  }
  
  // 7/1 ARM
  if (ltv <= 95 && creditScore >= 640) {
    scenarios.push({
      id: 'arm-7-1',
      productName: '7/1 ARM',
      programType: 'Conventional ARM',
      term: 360,
      amortizationType: 'Adjustable',
      interestRate: baseRate - 0.875,
      apr: baseRate - 0.250,
      points: 0,
      credits: loanAmount * 0.0125,
      monthlyPayment: calculateMonthlyPayment(loanAmount, baseRate - 0.875, 360),
      closingCosts: loanAmount * 0.02,
      eligible: true,
      eligibilityNotes: 'ARM qualified',
      pmi: ltv > 80 ? (loanAmount * 0.0055 / 12) : 0,
      features: ['Lower initial rate', 'Fixed for 7 years', 'Rate adjusts annually after']
    });
  }
  
  return scenarios.filter(s => s !== null);
};

/**
 * Calculates base interest rate based on credit and risk factors
 */
const calculateBaseRate = (creditScore, ltv, occupancy) => {
  let baseRate = 6.750; // Starting base rate
  
  // Credit score adjustments
  if (creditScore >= 780) baseRate -= 0.375;
  else if (creditScore >= 740) baseRate -= 0.250;
  else if (creditScore >= 700) baseRate -= 0.125;
  else if (creditScore < 640) baseRate += 0.500;
  else if (creditScore < 680) baseRate += 0.250;
  
  // LTV adjustments
  if (ltv > 95) baseRate += 0.500;
  else if (ltv > 90) baseRate += 0.375;
  else if (ltv > 85) baseRate += 0.250;
  else if (ltv > 80) baseRate += 0.125;
  else if (ltv <= 60) baseRate -= 0.125;
  
  // Occupancy adjustments
  if (occupancy === 'Second') baseRate += 0.375;
  else if (occupancy === 'Investment') baseRate += 0.750;
  
  return parseFloat(baseRate.toFixed(3));
};

/**
 * Calculates monthly payment (P&I only)
 */
const calculateMonthlyPayment = (principal, annualRate, termMonths) => {
  const monthlyRate = annualRate / 100 / 12;
  const payment = principal * (monthlyRate * Math.pow(1 + monthlyRate, termMonths)) / 
                  (Math.pow(1 + monthlyRate, termMonths) - 1);
  return parseFloat(payment.toFixed(2));
};

/**
 * Generates AI insights for pricing scenarios
 * @param {Object} inputs - Pricing inputs
 * @param {Array} scenarios - Pricing scenarios
 * @returns {Object} AI insights
 */
export const generateAIInsights = (inputs, scenarios) => {
  const { creditScore, loanAmount, propertyValue, loanPurpose, occupancy } = inputs;
  const ltv = ((loanAmount / propertyValue) * 100).toFixed(2);
  
  const insights = {
    summary: '',
    recommendations: [],
    warnings: [],
    opportunities: [],
    ineligibilityReasons: {}
  };
  
  // Analyze eligibility
  const eligibleScenarios = scenarios.filter(s => s.eligible);
  const ineligibleScenarios = scenarios.filter(s => !s.eligible);
  
  // Summary
  insights.summary = `Based on ${creditScore} credit score, ${ltv}% LTV, and ${occupancy} occupancy, ` +
    `${eligibleScenarios.length} of ${scenarios.length} products are eligible.`;
  
  // Ineligibility reasons
  ineligibleScenarios.forEach(scenario => {
    if (scenario.ineligibleReasons) {
      insights.ineligibilityReasons[scenario.productName] = scenario.ineligibleReasons;
    }
  });
  
  // Credit score recommendations
  if (creditScore < 740) {
    insights.recommendations.push({
      type: 'credit',
      title: 'Improve Credit Score',
      description: `Current score: ${creditScore}. Increasing to 740+ could reduce rate by 0.25% or more.`,
      impact: 'Rate reduction of 0.25% - 0.375%',
      effort: creditScore >= 700 ? 'Medium' : 'High'
    });
  }
  
  // LTV recommendations
  if (ltv > 80) {
    const targetDownPayment = propertyValue * 0.20;
    const additionalNeeded = targetDownPayment - (propertyValue - loanAmount);
    insights.recommendations.push({
      type: 'ltv',
      title: 'Reduce LTV to 80%',
      description: `Additional ${additionalNeeded.toLocaleString('en-US', { style: 'currency', currency: 'USD' })} down payment would eliminate PMI.`,
      impact: `Save $${((loanAmount * 0.0055 / 12).toFixed(2))}/month in PMI`,
      effort: 'High'
    });
  }
  
  // Product comparison insights
  if (eligibleScenarios.length > 1) {
    const lowestRate = Math.min(...eligibleScenarios.map(s => s.interestRate));
    const lowestPayment = Math.min(...eligibleScenarios.map(s => s.monthlyPayment));
    const bestRate = eligibleScenarios.find(s => s.interestRate === lowestRate);
    const bestPayment = eligibleScenarios.find(s => s.monthlyPayment === lowestPayment);
    
    if (bestRate.id !== bestPayment.id) {
      insights.opportunities.push({
        type: 'comparison',
        title: 'Rate vs Payment Tradeoff',
        description: `${bestRate.productName} has the lowest rate (${bestRate.interestRate}%), ` +
          `but ${bestPayment.productName} has the lowest payment ($${bestPayment.monthlyPayment}/mo).`,
        recommendation: 'Consider borrower\'s priority: lowest rate or lowest payment?'
      });
    }
  }
  
  // 15-year vs 30-year insight
  const conv15 = eligibleScenarios.find(s => s.id === 'conv-15-fixed');
  const conv30 = eligibleScenarios.find(s => s.id === 'conv-30-fixed');
  if (conv15 && conv30) {
    const paymentDiff = conv15.monthlyPayment - conv30.monthlyPayment;
    const totalInterest30 = (conv30.monthlyPayment * 360) - loanAmount;
    const totalInterest15 = (conv15.monthlyPayment * 180) - loanAmount;
    const interestSavings = totalInterest30 - totalInterest15;
    
    insights.opportunities.push({
      type: 'term',
      title: '15-Year Term Opportunity',
      description: `15-year term saves ${interestSavings.toLocaleString('en-US', { style: 'currency', currency: 'USD' })} ` +
        `in total interest but increases payment by ${paymentDiff.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}/month.`,
      recommendation: 'Evaluate if borrower can afford higher payment for significant savings'
    });
  }
  
  // Cash-out vs rate-and-term
  if (loanPurpose === 'Refinance') {
    insights.opportunities.push({
      type: 'purpose',
      title: 'Refinance Strategy',
      description: 'Rate-and-term refinance typically offers better pricing than cash-out.',
      recommendation: 'If borrower needs cash, evaluate if separate HELOC might be more cost-effective'
    });
  }
  
  // Occupancy impact
  if (occupancy !== 'Primary') {
    insights.warnings.push({
      type: 'occupancy',
      title: `${occupancy} Residence Pricing`,
      description: `${occupancy} occupancy adds ${occupancy === 'Second' ? '0.375%' : '0.750%'} to rates.`,
      note: 'Verify occupancy is accurate - primary residence offers best pricing'
    });
  }
  
  return insights;
};

/**
 * Saves a pricing scenario
 * @param {Object} scenario - Scenario to save
 * @param {string} name - Scenario name
 * @returns {Promise<Object>} Saved scenario
 */
export const saveScenario = async (scenario, name) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      const saved = {
        id: `scenario-${Date.now()}`,
        name: name,
        savedDate: new Date().toISOString(),
        ...scenario
      };
      resolve(saved);
    }, 300);
  });
};

/**
 * Parses MISMO XML for pricing inputs
 * @param {Document} xmlDoc - Parsed XML document
 * @returns {Object} Pricing inputs extracted from MISMO
 */
export const parseMISMOForPricing = (xmlDoc) => {
  const getTextContent = (selector, defaultValue = '') => {
    const element = xmlDoc.querySelector(selector);
    return element ? element.textContent.trim() : defaultValue;
  };
  
  const loanAmount = parseFloat(getTextContent('LOAN_AMOUNT, LOAN LoanAmount, TERMS_OF_LOAN LoanAmount')) || 0;
  const propertyValue = parseFloat(getTextContent('SALES_PRICE, PROPERTY SalesContractAmount, PROPERTY PropertyValuationAmount')) || 0;
  const loanPurpose = getTextContent('LOAN_PURPOSE_TYPE, LOAN LoanPurposeType') || 'Purchase';
  const creditScore = parseInt(getTextContent('CREDIT_SCORE, BORROWER CreditScoreValue')) || 700;
  const propertyState = getTextContent('PROPERTY ADDRESS StateCode, SUBJECT_PROPERTY ADDRESS StateCode') || 'CA';
  const occupancy = getTextContent('PROPERTY OccupancyType, SUBJECT_PROPERTY OccupancyType') || 'Primary';
  
  return {
    creditScore,
    occupancy: occupancy === 'PrimaryResidence' ? 'Primary' : occupancy === 'SecondHome' ? 'Second' : 'Investment',
    propertyState,
    propertyType: 'SingleFamily',
    units: 1,
    firstTimeBuyer: false,
    loanPurpose: loanPurpose === 'Purchase' ? 'Purchase' : 'Refinance',
    loanType: 'Mortgage',
    loanAmount,
    propertyValue,
    cashOutAmount: 0,
    term: 360,
    amortizationType: 'Fixed',
    lockPeriod: 30
  };
};

// Export constants
export const OCCUPANCY_TYPES = {
  PRIMARY: 'Primary',
  SECOND: 'Second',
  INVESTMENT: 'Investment'
};

export const PROPERTY_TYPES = {
  SINGLE_FAMILY: 'SingleFamily',
  CONDO: 'Condo',
  TOWNHOUSE: 'Townhouse',
  MULTI_FAMILY_2: 'TwoUnit',
  MULTI_FAMILY_3: 'ThreeUnit',
  MULTI_FAMILY_4: 'FourUnit'
};

export const LOAN_PURPOSES = {
  PURCHASE: 'Purchase',
  REFINANCE: 'Refinance'
};

export const LOAN_TYPES = {
  MORTGAGE: 'Mortgage',
  HELOC: 'HELOC',
  HOME_EQUITY_LOAN: 'HomeEquityLoan'
};
